import { FormulaErrorCodes, FormulaParseError, isWarning } from './errors.js';
import { typeCheck } from './checker.js';
import { DEFAULT_MAX_NESTING, parse } from './parser.js';
import { resolve } from './resolver.js';
export * from './span.js';
export * from './ast.js';
export * from './errors.js';
export * from './types.js';
export { DEFAULT_MAX_NESTING, parse } from './parser.js';
export { print } from './printer.js';
export { AST_VERSION, fromJson, toJson } from './json.js';
export { lookupSymbol, resolve } from './resolver.js';
export { FormulaProfile } from './profile.js';
export { typeCheck } from './checker.js';
export { allFunctions, findFunction, whereOf } from './registry.js';
export { FormulaDecimal } from './decimal.js';
export { evaluate } from './interpreter.js';
export { CellAggregation } from './aggregation.js';
export { isDecimal, isRows, toValue, truthy } from './values.js';
export { emitCSharp, cSharpType, cSharpStringLiteral } from './emit/csharp.js';
export { emitTypeScript, typeScriptTypeName, typeScriptTypeOfKind } from './emit/typescript.js';
export { aggregateCalls, emitSqlAggregate, isAggregateCall } from './emit/sql.js';
/** Parses with the nesting bound of §8: the profile's depth limit when the caller has one, else `DEFAULT_MAX_NESTING`. */
export function tryParse(text, maxNesting = DEFAULT_MAX_NESTING) {
    try {
        return { root: parse(text, maxNesting), error: null };
    }
    catch (e) {
        if (e instanceof FormulaParseError)
            return { root: null, error: e.error };
        throw e;
    }
}
/** No errors; what a save of a created or changed formula requires. */
export function isValid(result) {
    return result.errors.every(isWarning);
}
/** The errors that leave nothing to evaluate: no tree, no symbol, no base type, outside the profile, over a limit, a cycle. */
const HARD_ERROR_CODES = new Set([
    FormulaErrorCodes.ParseError,
    FormulaErrorCodes.UnknownSymbol,
    FormulaErrorCodes.BaseTypeMismatch,
    FormulaErrorCodes.NotAllowedInProfile,
    FormulaErrorCodes.LimitExceeded,
    FormulaErrorCodes.Cycle,
]);
/**
 * The formula has a value: it parsed, resolved and has a base type, whatever its value types or fit to the
 * target. What a host that tolerates value-type failures on migrated formulas (§9.6, Phase 1) evaluates.
 */
export function canEvaluate(result) {
    return result.root !== null && result.resultType !== null && result.errors.every((e) => isWarning(e) || !HARD_ERROR_CODES.has(e.code));
}
/**
 * The same check over a stored AST (§8): profile, resolution (by the ids the references carry, then by path) and
 * type check. The length limit does not apply — there is no text.
 */
export function checkAst(root, symbols, profile, target = null) {
    return checkParsed(root, null, symbols, profile, target);
}
/** Profile → resolve → type check over a tree, stopping after the first stage that reports errors. */
function checkParsed(root, textLength, symbols, profile, target) {
    const errors = profile.check(root, textLength);
    if (errors.length > 0)
        return { root, resultType: null, errors, nodeTypes: new Map() };
    const resolved = resolve(root, symbols, errors);
    if (errors.length > 0)
        return { root: resolved, resultType: null, errors, nodeTypes: new Map() };
    const nodeTypes = new Map();
    const type = typeCheck(resolved, symbols, target, errors, nodeTypes);
    return { root: resolved, resultType: type, errors, nodeTypes };
}
/**
 * Length → parse → profile → resolve → type check, stopping after the first stage that reports errors: a construct
 * the profile forbids makes its type moot, and an unknown symbol has no type to check. The length is checked before
 * the text is parsed, and the parse is bounded by the profile's depth (§8), so no text exhausts the stack.
 */
export function check(text, symbols, profile, target = null) {
    const tooLong = profile.checkLength(text);
    if (tooLong)
        return { root: null, resultType: null, errors: [tooLong], nodeTypes: new Map() };
    const parsed = tryParse(text, profile.maxNesting);
    if (!parsed.root)
        return { root: null, resultType: null, errors: [parsed.error], nodeTypes: new Map() };
    return checkParsed(parsed.root, null, symbols, profile, target);
}
